import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { useSupportService } from '~/services/api/support/useSupportService'
import type { SupportTopic } from '~/services/api/support/types'
import type { MenuSupportRequest } from '~/services/api/generated'
import { PreferredContactMethod } from '~/enums'
import { Normalization } from '~/utils/normalization'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'

const successAutoCloseMs = 9000
let successTimer: ReturnType<typeof setTimeout> | null = null

export const useMenuSupportStore = defineStore('menuSupport', () => {
  const support = useSupportService()
  const t = usePresentationTranslator()

  // State
  const isOpen = ref(false)
  const topic = ref<SupportTopic | ''>('')
  const name = ref('')
  const email = ref('')
  const phone = ref('')
  const preferredContactMethod = ref<PreferredContactMethod>(PreferredContactMethod.EMAIL)
  const subject = ref('')
  const message = ref('')
  const isSubmitting = ref(false)
  const submitProblemResolver = shallowRef<(() => AppProblemModel) | null>(null)
  const submitProblem = computed(() => submitProblemResolver.value?.() ?? null)
  const submitSuccess = ref(false)

  // Actions
  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
    reset()
  }

  function reset() {
    if (successTimer) {
      clearTimeout(successTimer)
      successTimer = null
    }
    topic.value = ''
    subject.value = ''
    message.value = ''
    isSubmitting.value = false
    submitProblemResolver.value = null
    submitSuccess.value = false
  }

  function selectTopic(value: SupportTopic) {
    topic.value = value
    // Map topic to subject label
    const labels: Record<SupportTopic, string> = {
      build_my_menu: 'Build My Menu',
      availability: 'Availability',
      pricing: 'Pricing',
      modifiers: 'Modifiers',
      technical_issue: 'Technical Issue'
    }
    subject.value = labels[value]
  }

  // Clears prefilled contact PII on identity loss; distinct from reset() so closing
  // the widget doesn't wipe a same-session guest's manually typed contact info.
  function clearContactInfo() {
    name.value = ''
    email.value = ''
    phone.value = ''
  }

  function prefill(userData: { name?: string; email?: string; phone?: string }) {
    const prefillName = Normalization.trimmedStringValue(userData.name)
    if (prefillName !== null) name.value = prefillName

    const prefillEmail = Normalization.trimmedStringValue(userData.email)
    if (prefillEmail !== null) email.value = prefillEmail

    const prefillPhone = Normalization.trimmedStringValue(userData.phone)
    if (prefillPhone !== null) phone.value = prefillPhone
  }

  async function submit(payload: MenuSupportRequest) {
    isSubmitting.value = true
    submitProblemResolver.value = null

    try {
      const response = await support.submitMenuHelp(payload)

      if (response.success) {
        submitSuccess.value = true
        // Auto-close after ~9 seconds
        if (successTimer) clearTimeout(successTimer)
        successTimer = setTimeout(() => {
          close()
          successTimer = null
        }, successAutoCloseMs)
      } else {
        submitProblemResolver.value = () =>
          AppProblemModel.fromDomainFailure(t('common.menuSupport.genericSubmitError'))
      }
    } catch (error: unknown) {
      const problem = AppProblemModel.normalize(error)
      submitProblemResolver.value = () => problem
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    // State
    isOpen,
    topic,
    name,
    email,
    phone,
    preferredContactMethod,
    subject,
    message,
    isSubmitting,
    submitProblem,
    submitSuccess,
    // Actions
    open,
    close,
    reset,
    clearContactInfo,
    selectTopic,
    prefill,
    submit
  }
})
